/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, {
    getCatalogId,
    IComponent,
    IComponentProps,
    IGridSettings,
    IImageSettings,
    //msdyn365Commerce,
    TelemetryEvent,
    IActionContext
} from '@msdyn365-commerce/core';
import {
    addProductToCartInternal,
    addProductsToCartInternal,
    getCartState,
    ICartActionResult,
    ICartActionResultWithCart,
    ICartState
} from '@msdyn365-commerce/global-state';
import {
    Address,
    CartLine,
    CartLineValidationResults,
    CartsDataActions,
    format,
    OrgUnitLocation,
    ProductAvailableQuantity,
    ProductDimension,
    ProductPrice,
    ProductsDataActions,
    ProductSearchCriteria,
    ProductType,
    SimpleProduct,
    ReleasedProductType,
    AttributeValue
} from '@msdyn365-commerce/retail-proxy';
import { ErrorNotification, NotificationsManager } from '@msdyn365-commerce-modules/notifications-core';
import { ArrayExtensions, generateProductImageUrl, ObjectExtensions, validateCatalogId } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, IPopupProps, ITelemetryContent, Popup } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React, { useState } from 'react';

import {
    PriceComponent,
    IItemsAddedToCartResources,
    ItemsAddedToCartDialogComponent,
    MultiItemsSuccessfullyAddedToCartNotification,
    OrderDetailsProduct,
    ItemSuccessfullyAddedToCartNotification
} from '@msdyn365-commerce/components';
import { FileUploadDialog } from '../FileUploadDialog/FileUploadDialog';
import { necUploadImageAsync } from '../../proxy/DataActionExtension.g';
import { IProductTimeSlot, ITimeSlot } from '../../proxy/DataServiceEntities.g';
import { createTextAttribute } from '../../utils/attributes';
import {
    NEC_DOCUMENTATION_FILE_LINK_ATTR_NAME,
    NEC_SELECTED_TIME_SLOT_FROM,
    NEC_SELECTED_TIME_SLOT_ID_ATTR_NAME,
    NEC_SELECTED_TIME_SLOT_TEXT_ATTR_NAME,
    NEC_SELECTED_TIME_SLOT_TO
} from '../../constants';

/**
 * Interface for add to cart resources.
 */
export interface IAddToCartResources {
    goToCartText: string;
    continueShoppingText: string;
    closeNotificationLabel: string;
    headerItemOneText: string;
    headerItemFormatText: string;
    headerLinesFormatText?: string;
    headerMessageText: string;
    freePriceText: string;
    originalPriceText: string;
    currentPriceText: string;
    addedQuantityText: string;
    buyAgainNotificationTitle?: string;
    buyAgainNotificationCloseAriaLabel?: string;
    validationErrorMessage?: string;
    oneErrorText?: string;
    multiErrorsText?: string;
}

/**
 * Interface for add to cart components props.
 */
export interface IAddToCartComponentProps extends IComponentProps<{ product: SimpleProduct; price?: ProductPrice } | undefined> {
    className?: string;
    addToCartText: string;
    outOfStockText?: string;
    disabled?: boolean;
    quantity?: number;
    navigationUrl?: string;
    productAvailability?: ProductAvailableQuantity;
    getSelectedProduct?: Promise<SimpleProduct | null>;
    isNavigationToCartPageDisabled?: boolean;
    shouldSkipSiteSettings?: boolean;
    addToCartArialLabel?: string;
    products?: {
        productId: number;
        quantity: number;
        catalogId?: number;
    }[];
    orderDetailsProducts?: OrderDetailsProduct[];
    hasAvailableProducts?: boolean;
    imageSettings?: IImageSettings;
    gridSettings?: IGridSettings;

    isLoading?: boolean;
    isUpdatingDimension?: boolean;
    isLoadingDeliveryOptions?: boolean;
    isUpdatingDeliveryOptions?: boolean;
    isAddServiceItemToCart?: boolean;
    isAddEmailDeliveryItemToCart?: boolean;
    isPriceKeyedIn?: boolean;
    customPriceAmount?: number;
    isOrderQuantityLimitsFeatureEnabled?: boolean;

    dialogStrings?: IAddToCartResources;

    telemetryContent?: ITelemetryContent;

    catalogId?: number;

    isCustomPriceSelected?: boolean;
    maximumKeyInPrice?: number;
    minimumKeyInPrice?: number;
    defaultMaximumKeyInPrice?: number;
    defaultMinimumKeyInPrice?: number;
    isProductQuantityLoading?: boolean;
    selectedEvent?: IProductTimeSlot;
    selectedTimeSlot?: ITimeSlot;
    setTimeSlotError: () => void;

    onAdd?(result: ICartActionResult): void;
    onError?(result: IAddToCartFailureResult): void;
    changeUpdatingDimension?(isUpdatingDimension: boolean): void;
    changeUpdatingDeliveryOptions?(isUpdatingDeliveryOptions: boolean): void;
}

/**
 * Cart action Failure reason type.
 */
export declare type ICartActionFailureReason =
    | 'EMPTYINPUT'
    | 'MISSINGDIMENSION'
    | 'OUTOFSTOCK'
    | 'CARTACTIONFAILED'
    | 'INVALIDCUSTOMAMOUNT';

/**
 * Interface for add to cart failure result.
 */
export interface IAddToCartFailureResult {
    failureReason: ICartActionFailureReason;

    stockLeft?: number;
    cartActionResult?: ICartActionResult;
    missingDimensions?: ProductDimension[];
}

/**
 * This setting defines the experience when a product is added to cart. Corresponds to the configuration in Fabrikam.
 */
export enum AddToCartBehavior {
    /**
     * Navigate to cart page.
     */
    goToCart = 'goToCart',

    /**
     * Show item added to cart popup.
     */
    showModal = 'showModal',

    /**
     * Show mini cart popup.
     */
    showMiniCart = 'showMiniCart',

    /**
     * Show item added to cart notification.
     */
    showNotification = 'showNotification',

    /**
     * Do nothing and stay on the page.
     */
    nothing = 'nothing'
}

const zero = 0;
const defaultQuantity = 1;

/**
 * The propagate result.
 * @param props -- The add to cart component props.
 * @param result -- The cart action result.
 */
const propagateResult = (props: IAddToCartComponentProps, result: ICartActionResult): void => {
    if (props.onAdd) {
        props.onAdd(result);
    }
};

/**
 * The propagate error.
 * @param props -- The add to cart component props.
 * @param result -- The add to cart failure result.
 */
const propagateError = (props: IAddToCartComponentProps, result: IAddToCartFailureResult): void => {
    if (props.onError) {
        props.onError(result);
    }
};

/**
 * Retrieves add to cart input from the component props.
 * @param props - Add to cart component props.
 * @returns Add to cart input.
 */
const getAddToCartInputFromProps = async (props: IAddToCartComponentProps) => {
    const products = props.orderDetailsProducts?.filter(product => !ObjectExtensions.isNullOrUndefined(product.simpleProduct));
    const input = products?.map(product => {
        return {
            product: product.simpleProduct!,
            count: ObjectExtensions.isNullOrUndefined(product.salesLine.Quantity) ? defaultQuantity : product.salesLine.Quantity,
            catalogId: product.salesLine.CatalogId
        };
    });

    if (ArrayExtensions.hasElements(input)) {
        return input;
    }

    if (ArrayExtensions.hasElements(props.products)) {
        const actionContext = props.context.actionContext;
        const apiSettings = actionContext.requestContext.apiSettings;

        const catalogId = props.catalogId ?? getCatalogId(props.context.actionContext.requestContext);
        validateCatalogId(catalogId);

        const searchCriteriaInput: ProductSearchCriteria = {
            Context: {
                ChannelId: apiSettings.channelId,
                CatalogId: catalogId
            },
            IncludeAttributes: false,
            Ids: props.products.map(product => product.productId)
        };

        const searchResult = await ProductsDataActions.searchByCriteriaAsync({ callerContext: actionContext }, searchCriteriaInput);
        const productSearchResultsWithImages = searchResult.map(productSearchResult => {
            const newProductSearchResult = { ...productSearchResult };
            const newImageUrl = generateProductImageUrl(newProductSearchResult, apiSettings);

            if (newImageUrl) {
                newProductSearchResult.PrimaryImageUrl = newImageUrl;
            }

            return newProductSearchResult;
        });

        return props.products.map(item => {
            const productData = productSearchResultsWithImages.find(product => product.RecordId === item.productId);
            let simpleProduct: SimpleProduct;
            if (productData) {
                simpleProduct = {
                    ...productData,
                    ProductTypeValue: ProductType.Variant,
                    AdjustedPrice: productData.Price,
                    BasePrice: productData.BasePrice ?? productData.Price
                };
            } else {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Cast to simple product since only record id is available.
                simpleProduct = {
                    RecordId: item.productId
                } as SimpleProduct;
            }

            return {
                product: simpleProduct,
                count: item.quantity,
                catalogId: item.catalogId
            };
        });
    }

    if (props.data?.product) {
        return [
            {
                product: props.data.product,
                count: props.quantity ?? defaultQuantity,
                catalogId: props.catalogId
            }
        ];
    }

    return [];
};

/**
 * Add one item to cart when it is not buy again.
 * @param props -- The props.
 * @param setDisabled -- The set disable call back.
 * @param openModal -- The open modal call back.
 * @param attributes - Product attributes.
 */
const addOneItemToCart = async (
    props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    openModal: (opened: boolean) => void,
    attributes: AttributeValue[]
): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
    const addToCartBehavior = props.context.app.config.addToCartBehavior;
    const data = props.data;

    const addToCartInput = await getAddToCartInputFromProps(props);
    const product = addToCartInput[0].product;
    const quantity = addToCartInput[0].count;

    const cartState = await getCartState(props.context.actionContext);

    /*
     * replacing cartState.addProductToCart() with custom addProductToCart() function
     */
    const addToCartResult = await addProductToCart(
        props,
        {
            product,
            count: quantity,
            availableQuantity: props.productAvailability?.AvailableQuantity,
            additionalProperties: { orderQuantityLimitsFeatureIsEnabled: props.isOrderQuantityLimitsFeatureEnabled },
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
            enableStockCheck: props.context.app.config.enableStockCheck,
            isPriceKeyedIn: props.isPriceKeyedIn,
            customPrice: props.isPriceKeyedIn ? props.customPriceAmount : undefined,
            isAddEmailDeliveryItemToCart: props.isAddEmailDeliveryItemToCart,
            shouldSkipSiteSettings: props.shouldSkipSiteSettings,
            catalogId: addToCartInput[0].catalogId
        },
        cartState,
        attributes
    );

    if (addToCartResult.status === 'SUCCESS') {
        if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showModal) {
            setDisabled(false);
            openModal(true);
        } else if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showNotification) {
            setDisabled(false);
            const notification = new ItemSuccessfullyAddedToCartNotification(
                props.context,
                props.dialogStrings,
                props.imageSettings,
                props.gridSettings,
                product,
                data?.price,
                quantity,
                props.navigationUrl,
                props.telemetryContent!,
                props.id,
                props.typeName
            );
            NotificationsManager.instance().addNotification(notification);
        } else if (
            MsDyn365.isBrowser &&
            props.navigationUrl &&
            !props.isNavigationToCartPageDisabled &&
            (addToCartBehavior === undefined || addToCartBehavior === AddToCartBehavior.goToCart)
        ) {
            window.location.assign(props.navigationUrl);
        } else {
            setDisabled(false);
        }
        propagateResult(props, addToCartResult);
    } else {
        NotificationsManager.instance().addNotification(
            new ErrorNotification(
                addToCartResult.errorDetails?.LocalizedMessage ?? 'Add to cart failed',
                props.dialogStrings?.closeNotificationLabel ?? ''
            )
        );

        propagateError(props, { failureReason: 'CARTACTIONFAILED', cartActionResult: addToCartResult });
        setDisabled(false);
    }
};

/**
 * Callback to handle success of adding to cart.
 * @param props - Add to cart component props.
 * @param setDisabled - Callback to update disabled state of the component.
 * @param setItemsAddedToCartDialogOpen - Callback to update disabled state of the component.
 * @param setErrorMessage - Callback to update error message state of the component.
 * @param cartState - Current cart state.
 * @param addToCartInput - Input used for adding to cart.
 * @param addToCartResult - Result of adding to cart.
 */
const handleAddItemsToCartSuccess = async (
    props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    setItemsAddedToCartDialogOpen: (opened: boolean) => void,
    setErrorMessage: (message: string) => void,
    cartState: ICartState,
    addToCartInput: {
        product: SimpleProduct;
        count: number;
    }[],
    addToCartResult: ICartActionResult
) => {
    // Validate cart for line errors.
    const validationResult: CartLineValidationResults = await CartsDataActions.validateForCheckoutAsync(
        { callerContext: props.context.actionContext, bypassCache: 'none' },
        cartState.cart.Id,
        cartState.cart.Version
    );
    const errorCount = (validationResult.ValidationFailuresByCartLines ?? []).length;
    const singleErrorCount = 1;
    const errorText = errorCount === singleErrorCount ? props.dialogStrings?.oneErrorText : props.dialogStrings?.multiErrorsText;
    const errorMessage =
        errorCount > zero ? format(props.dialogStrings?.validationErrorMessage ?? '', errorCount.toString(), errorText) : '';

    setErrorMessage(errorMessage);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
    const addToCartBehavior = props.context.app.config.addToCartBehavior;

    if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showModal) {
        setDisabled(false);
        setItemsAddedToCartDialogOpen(true);
    } else if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showNotification) {
        setDisabled(false);
        const itemsAddedResource: IItemsAddedToCartResources = {
            viewCartButtonText: props.dialogStrings.goToCartText,
            closeButtonLabel: props.dialogStrings.closeNotificationLabel,
            itemsAddedToCartHeaderText: props.dialogStrings.headerMessageText,
            itemsAddedToCartFormatText: props.dialogStrings.headerItemFormatText,
            linesAddedToCartFormatText: props.dialogStrings.headerLinesFormatText ?? '{0}',
            itemsAddedValidationErrorMessage: errorMessage
        };

        const notification = new MultiItemsSuccessfullyAddedToCartNotification(
            props.context,
            itemsAddedResource,
            props.imageSettings,
            props.gridSettings,
            addToCartInput,
            props.navigationUrl,
            props.telemetryContent!,
            props.id,
            props.typeName
        );
        NotificationsManager.instance().addNotification(notification);
    } else if (
        MsDyn365.isBrowser &&
        props.navigationUrl &&
        !props.isNavigationToCartPageDisabled &&
        (addToCartBehavior === undefined || addToCartBehavior === AddToCartBehavior.goToCart)
    ) {
        window.location.assign(props.navigationUrl);
    } else {
        setDisabled(false);
    }
    propagateResult(props, addToCartResult);
};

/*
 *
 * Start copy logic from node_modules\@msdyn365-commerce\global-state\src\cart-state\base-cart-state.ts
 *  ===================================================================================================
 *
 * */

const buildAddressFromOrgUnitLocation = (location: OrgUnitLocation): Address => {
    return {
        RecordId: location.PostalAddressId,
        Name: location.OrgUnitName,
        FullAddress: location.Address,
        Street: location.Street,
        StreetNumber: location.StreetNumber,
        City: location.City,
        DistrictName: location.DistrictName,
        BuildingCompliment: location.BuildingCompliment,
        Postbox: location.Postbox,
        ThreeLetterISORegionName: location.Country,
        ZipCode: location.Zip,
        County: location.County,
        CountyName: location.CountyName,
        State: location.State,
        StateName: location.StateName
    };
};

const doCartOperationWithRetry = async (
    callback: () => Promise<ICartActionResultWithCart>,
    cartState: ICartState
): Promise<ICartActionResult> => {
    let callbackResult = await callback();

    if (callbackResult.status === 'SUCCESS') {
        if (callbackResult.cart) {
            await cartState.updateCart({ newCartObject: callbackResult.cart });
        }
    } else {
        const refreshCartResult = await cartState.refreshCart({});

        if (refreshCartResult.status === 'SUCCESS') {
            callbackResult = await callback();

            if (callbackResult.status === 'SUCCESS') {
                if (callbackResult.cart) {
                    await cartState.updateCart({ newCartObject: callbackResult.cart });
                }
            }
        }
    }

    /*
     * TODO: do we need it?
     * */
    // if (callbackResult.status === 'SUCCESS' && callbackResult.substatus && callbackResult.substatus === 'PRODUCTADDED') {
    //     this._isProductAddedToCart = true;
    // } else {
    //     this._isProductAddedToCart = false;
    // }

    return {
        status: callbackResult.status,
        substatus: callbackResult.substatus,
        errorDetails: callbackResult.errorDetails,
        validationResults: callbackResult.validationResults
    };
};

const addProductToCart = async (
    props: IAddToCartComponentProps,
    input: {
        product: SimpleProduct;
        count?: number;
        location?: OrgUnitLocation;
        additionalProperties?: object;
        availableQuantity?: number;
        enableStockCheck?: boolean;
        isAddEmailDeliveryItemToCart?: boolean;
        isPriceKeyedIn?: boolean;
        customPrice?: number;
        deliveryMode?: string;
        shouldSkipSiteSettings?: boolean;
        catalogId?: number;
        retailMultiplePickupModeEnabled?: boolean;
    },
    cartState: ICartState,
    attributes: AttributeValue[]
): Promise<ICartActionResult> => {
    const cartLine: CartLine = {
        CatalogId: input.catalogId ?? getCatalogId(props.context.actionContext.requestContext),
        Description: input.product.Description,

        // TODO: Investigate this value and what it represents
        EntryMethodTypeValue: 3,
        ItemId: input.product.ItemId,
        ProductId: input.product.RecordId,
        Quantity: input.count || 1,
        TrackingId: '',
        UnitOfMeasureSymbol: input.product.DefaultUnitOfMeasure,
        IsPriceKeyedIn: input.isPriceKeyedIn,
        IsGiftCardLine: input.product.IsGiftCard,
        Price: input.customPrice ? input.customPrice : input.product.Price,
        AttributeValues: attributes
    };

    if (input.location) {
        if (!props.context.actionContext.requestContext.channel) {
            return { status: 'FAILED' };
        }

        // If curbside pick is not available use the default one
        if (input.deliveryMode !== undefined) {
            cartLine.DeliveryMode = input.deliveryMode;
        } else {
            cartLine.DeliveryMode = input.retailMultiplePickupModeEnabled
                ? undefined
                : props.context.actionContext.requestContext.channel.PickupDeliveryModeCode;
        }

        cartLine.FulfillmentStoreId = input.location.OrgUnitNumber;
        cartLine.WarehouseId = input.location.InventoryLocationId;
        cartLine.ShippingAddress = buildAddressFromOrgUnitLocation(input.location);
    }

    if (input.isAddEmailDeliveryItemToCart) {
        if (!props.context.actionContext.requestContext.channel) {
            return { status: 'FAILED' };
        }

        cartLine.DeliveryMode = props.context.actionContext.requestContext.channel.EmailDeliveryModeCode;
    }

    // Check if the product is service or not by product type
    const isAddServiceItemToCart = input.product.ItemTypeValue === ReleasedProductType.Service;

    // @ts-expect-error
    const orderQuantityLimitsFeatureIsEnabled: boolean = !!input.additionalProperties?.orderQuantityLimitsFeatureIsEnabled;
    // commented after ssk 9.53
    //const isMaxByQuantityValidationFeatureDefined: boolean = !!input.product.Behavior?.MaximumQuantity;
    return doCartOperationWithRetry(
        () =>
            addProductToCartInternal(
                cartState.cart,
                cartLine,
                props.context.actionContext,
                orderQuantityLimitsFeatureIsEnabled,
                input.availableQuantity,
                input.enableStockCheck,
                isAddServiceItemToCart,
                //isMaxByQuantityValidationFeatureDefined,
                input.shouldSkipSiteSettings
            ),
        cartState
    );
};

const addProductsToCart = async (
    props: IAddToCartComponentProps,
    input: {
        product: SimpleProduct;
        count?: number;
        location?: OrgUnitLocation;
        additionalProperties?: object;
        availableQuantity?: number;
        enableStockCheck?: boolean;
        isAddEmailDeliveryItemToCart?: boolean;
        isPriceKeyedIn?: boolean;
        customPrice?: number;
        deliveryMode?: string;
        catalogId?: number;
        retailMultiplePickupModeEnabled?: boolean;
    }[],
    cartState: ICartState,
    attributes: AttributeValue[]
): Promise<ICartActionResult> => {
    const internalInput: {
        cartLineToAdd: CartLine;
        availableQuantity?: number;
        isStockCheckEnabled?: boolean;
        isAddServiceItemToCart?: boolean;
    }[] = [];

    for (const inputItem of input) {
        const cartLine: CartLine = {
            CatalogId: inputItem.catalogId ?? getCatalogId(props.context.actionContext.requestContext),
            Description: inputItem.product.Description,

            // TODO: Investigate this value and what it represents
            EntryMethodTypeValue: 3,
            ItemId: inputItem.product.ItemId,
            ProductId: inputItem.product.RecordId,
            Quantity: inputItem.count || 1,
            TrackingId: '',
            UnitOfMeasureSymbol: inputItem.product.DefaultUnitOfMeasure,
            IsPriceKeyedIn: inputItem.isPriceKeyedIn,
            IsGiftCardLine: inputItem.product.IsGiftCard,
            Price: inputItem.customPrice ? inputItem.customPrice : inputItem.product.Price,
            AttributeValues: attributes
        };

        if (inputItem.location) {
            if (!props.context.actionContext.requestContext.channel) {
                return { status: 'FAILED' };
            }

            // If curbside pick is not available use the default one
            if (inputItem.deliveryMode !== undefined) {
                cartLine.DeliveryMode = inputItem.deliveryMode;
            } else {
                cartLine.DeliveryMode = inputItem.retailMultiplePickupModeEnabled
                    ? undefined
                    : props.context.actionContext.requestContext.channel.PickupDeliveryModeCode;
            }

            cartLine.FulfillmentStoreId = inputItem.location.OrgUnitNumber;
            cartLine.WarehouseId = inputItem.location.InventoryLocationId;
            cartLine.ShippingAddress = buildAddressFromOrgUnitLocation(inputItem.location);
        }

        if (inputItem.isAddEmailDeliveryItemToCart) {
            cartLine.DeliveryMode = props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode;
        }

        // Check if the product is service or not by product type
        const PRODUCTASSERVICE = 2 as ReleasedProductType.Service;
        const isAddServiceItemToCart = inputItem.product.ItemTypeValue === PRODUCTASSERVICE;

        internalInput.push({
            cartLineToAdd: cartLine,
            availableQuantity: inputItem.availableQuantity,
            isStockCheckEnabled: inputItem.enableStockCheck,
            isAddServiceItemToCart
        });
    }

    return doCartOperationWithRetry(() => addProductsToCartInternal(cartState.cart, props.context.actionContext, internalInput), cartState);
};

/*
 *
 * END ===================================================================================================
 *
 * */

/**
 * Add one or multi item to cart from buy again.
 * @param props -- The props.
 * @param setDisabled -- The set disable call back.
 * @param setItemsAddedToCartDialogOpen -- The open modal call back.
 * @param setErrorMessage -- The set error message call back.
 * @param attributes - Product attributes.
 */
const addItemsToCart = async (
    props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    setItemsAddedToCartDialogOpen: (opened: boolean) => void,
    setErrorMessage: (message: string) => void,
    attributes: AttributeValue[]
): Promise<void> => {
    const addToCartInput = await getAddToCartInputFromProps(props);

    if (!ArrayExtensions.hasElements(addToCartInput)) {
        return;
    }

    const cartState = await getCartState(props.context.actionContext);

    /*
     * replacing cartState.addProductsToCart() with custom addProductsToCart() function
     */
    const addToCartResult = await addProductsToCart(props, addToCartInput, cartState, attributes);

    if (addToCartResult.status === 'SUCCESS') {
        await handleAddItemsToCartSuccess(
            props,
            setDisabled,
            setItemsAddedToCartDialogOpen,
            setErrorMessage,
            cartState,
            addToCartInput,
            addToCartResult
        );
    } else {
        if (props.dialogStrings?.buyAgainNotificationTitle) {
            // For buy again show simple error notification.
            NotificationsManager.instance().addNotification(
                new ErrorNotification(
                    props.dialogStrings.buyAgainNotificationTitle,
                    props.dialogStrings.buyAgainNotificationCloseAriaLabel ?? ''
                )
            );
        } else {
            NotificationsManager.instance().addNotification(
                new ErrorNotification(
                    addToCartResult.errorDetails?.LocalizedMessage ?? 'Add to cart failed',
                    props.dialogStrings?.closeNotificationLabel ?? ''
                )
            );
        }

        propagateError(props, { failureReason: 'CARTACTIONFAILED', cartActionResult: addToCartResult });
        setDisabled(false);
    }
};

/**
 * Interface for add to cart component.
 */
export interface IAddtoCartComponent extends IComponent<IAddToCartComponentProps> {
    onClick(): (event: React.MouseEvent<HTMLElement>, props: IAddToCartComponentProps) => void;
}

/**
 * On click function.
 * @param _event - The mouse event.
 * @param props - The props.
 * @param setDisabled - Flag to define whether the element is disabled.
 * @param openModal - Flag to specify if it should open in a modal window.
 * @param setItemsAddedToCartDialogOpen - Sets items added to cart while dialog is open.
 * @param setErrorMessage - Error message.
 * @param attributes - Product attributes.
 */
const onClick = async (
    _event: React.MouseEvent<HTMLElement>,
    props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    openModal: (opened: boolean) => void,
    setItemsAddedToCartDialogOpen: (opened: boolean) => void,
    setErrorMessage: (message: string) => void,
    attributes: AttributeValue[]
): Promise<void> => {
    if (!ArrayExtensions.hasElements(props.products)) {
        const cartError = addToCartError(props);

        if (cartError) {
            propagateError(props, cartError);
            return;
        }

        setDisabled(true);
    }

    const hasOrderDetailsProducts =
        ArrayExtensions.hasElements(props.orderDetailsProducts) && props.orderDetailsProducts.length > defaultQuantity;
    const hasProducts = ArrayExtensions.hasElements(props.products) && props.products.length > defaultQuantity;

    const hasMultipleProducts = hasOrderDetailsProducts || hasProducts;

    if (props.shouldSkipSiteSettings && hasMultipleProducts) {
        await addItemsToCart(props, setDisabled, setItemsAddedToCartDialogOpen, setErrorMessage, attributes);
    } else {
        await addOneItemToCart(props, setDisabled, openModal, attributes);
    }
};

/**
 * Add to cart component action constant.
 */
const AddToCartComponentActions = {
    onClick
};

const useConfirmDialog = <T,>() => {
    const [promise, setPromise] = useState<null | { resolve: (value: T | PromiseLike<T>) => void }>(null);
    const [isOpen, setIsOpen] = useState(false);

    const getConfirm = (): Promise<T> =>
        new Promise(resolve => {
            setIsOpen(true);
            setPromise({ resolve });
        });

    const handleClose = () => {
        setIsOpen(false);
        setPromise(null);
    };

    const handleConfirm = (response: T) => {
        promise?.resolve(response);
        handleClose();
    };

    const handleCancel = (response: T) => {
        promise?.resolve(response);
        handleClose();
    };

    return { getConfirm, isOpen, handleConfirm, handleCancel };
};

const convertFileToBase64 = (file: File) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
    });

const uploadFile = async (actionContext: IActionContext, file: File) => {
    const context = { callerContext: actionContext };

    try {
        const encodedFileInBase64 = ((await convertFileToBase64(file)) as unknown) as string;

        const base64result = encodedFileInBase64.split(',')[1];
        const fileName = file.name;

        let imageUrl = '';

        try {
            const imageData = await necUploadImageAsync(context, base64result, fileName);
            if (imageData && imageData.imageUrl) {
                imageUrl = imageData.imageUrl;
            }
        } catch {
            imageUrl = '';
        }

        return imageUrl;
    } catch (error) {
        throw error;
    }
};

/**
 * Add to cart component.
 * @param props - The props.
 * @returns - The add to cart component.
 */
export const AddToCartFunctionalComponent: React.FC<IAddToCartComponentProps> = (props: IAddToCartComponentProps) => {
    const [disabled, setDisabled] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [isItemsAddedToCartDialogOpen, setItemsAddedToCartDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [addToCartInput, setAddToCartInput] = useState<
        {
            product: SimpleProduct;
            count: number;
        }[]
    >([]);

    const { getConfirm, isOpen, handleConfirm, handleCancel } = useConfirmDialog<string | null>();

    const onUploadFileClick = async (files: File[] | null, clearFilesFromState: () => void) => {
        if (files?.length) {
            uploadFile(props.context.actionContext, files[0])
                .then(link => {
                    if (link) {
                        handleConfirm(link);
                        clearFilesFromState();
                    }
                })
                .catch(() => {
                    console.log('file did not upload');
                    handleCancel(null);
                });
        }
    };

    const onCancelClick = () => {
        handleCancel(null);
    };

    const onClickHandler = async (event: React.MouseEvent<HTMLElement>) => {
        const catalogId = props.catalogId ?? getCatalogId(props.context.actionContext.requestContext);
        validateCatalogId(catalogId);

        if (props.data?.product) {
            const attributes = await ProductsDataActions.getAttributeValuesAsync(
                { callerContext: props.context.actionContext },
                props.data?.product.RecordId,
                props.context.actionContext.requestContext.apiSettings.channelId,
                catalogId ?? getCatalogId(props.context.actionContext.requestContext)
            );

            const fileRequiredAttribute = attributes.find(attribute => attribute.Name === 'Upload Documentation');
            const newAttributes: AttributeValue[] = [];

            if (fileRequiredAttribute?.TextValue?.toLowerCase() === 'yes') {
                const fileLinkFromConfirm = await getConfirm();

                if (fileLinkFromConfirm) {
                    const newFileLinkAttribute = createTextAttribute(NEC_DOCUMENTATION_FILE_LINK_ATTR_NAME, fileLinkFromConfirm);
                    newAttributes.push(newFileLinkAttribute);
                } else {
                    console.log('show error - upload file to the product');
                    return;
                }
            }

            if (props.selectedEvent && props.selectedTimeSlot) {
                const timeSlotIdAttribute = createTextAttribute(NEC_SELECTED_TIME_SLOT_ID_ATTR_NAME, props.selectedEvent.RecId?.toString());
                const timeSlotFromAttribute = createTextAttribute(NEC_SELECTED_TIME_SLOT_FROM, props.selectedTimeSlot.ValueFrom ?? '');
                const timeSlotToAttribute = createTextAttribute(NEC_SELECTED_TIME_SLOT_TO, props.selectedTimeSlot.ValueTo ?? '');
                const timeSlotTextAttribute = createTextAttribute(
                    NEC_SELECTED_TIME_SLOT_TEXT_ATTR_NAME,
                    `${props?.selectedEvent?.SlotName} ${props?.selectedTimeSlot?.Name}`
                );
                newAttributes.push(timeSlotIdAttribute, timeSlotTextAttribute, timeSlotFromAttribute, timeSlotToAttribute);
            } else {
                props.setTimeSlotError();

                return;
            }

            await AddToCartComponentActions.onClick(
                event,
                props,
                setDisabled,
                setModalOpen,
                setItemsAddedToCartDialogOpen,
                setErrorMessage,
                newAttributes
            );
        }
    };

    /**
     * Close dialog.
     */
    const closeItemsAddedToCartDialog = React.useCallback(() => {
        setItemsAddedToCartDialogOpen(false);
    }, []);

    const priceComponent = props.data?.price ? (
        <PriceComponent
            data={{ price: props.data.price }}
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            freePriceText={props.dialogStrings?.freePriceText}
            originalPriceText={props.dialogStrings?.originalPriceText}
            currentPriceText={props.dialogStrings?.currentPriceText}
        />
    ) : (
        ''
    );

    const popupProps: IPopupProps = {
        context: props.context,
        className: 'msc-add-to-cart',
        id: props.id,
        typeName: props.typeName,
        data: { product: addToCartInput[0]?.product, price: props.data?.price }, // addToCartInput[0]?.product?.Price },
        dialogStrings: props.dialogStrings,
        imageSettings: props.imageSettings,
        gridSettings: props.context.request.gridSettings,
        productQuantity: addToCartInput[0]?.count !== undefined ? addToCartInput[0]?.count : defaultQuantity,
        priceComponent,
        navigationUrl: props.navigationUrl,
        modalOpen,
        setModalOpen,
        telemetryContent: props.telemetryContent
    };

    const itemsAddedResource: IItemsAddedToCartResources = {
        viewCartButtonText: props.dialogStrings?.goToCartText ?? '',
        closeButtonLabel: props.dialogStrings?.closeNotificationLabel ?? 'Close',
        itemsAddedToCartHeaderText: props.dialogStrings?.headerMessageText ?? '',
        itemsAddedToCartFormatText: props.dialogStrings?.headerItemFormatText ?? '',
        linesAddedToCartFormatText: props.dialogStrings?.headerLinesFormatText ?? '{0}',
        itemsAddedValidationErrorMessage: errorMessage
    };

    const renderModalPopup = <Popup {...popupProps} />;
    const label = getLinkText(props);
    const payload = getPayloadObject(TelemetryEvent.AddToCart, props.telemetryContent!, label, '');
    const attributes = getTelemetryAttributes(props.telemetryContent!, payload);
    const buttonClassName = props.shouldSkipSiteSettings ? '' : 'msc-add-to-cart ';

    React.useEffect(() => {
        /**
         * Retrieves add to cart input and updates the state.
         */
        const retrieveAddToCartInput = async () => {
            const retrievedInput = await getAddToCartInputFromProps(props);
            setAddToCartInput(retrievedInput);
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Call async method as per the documentation of react useEffect.
        retrieveAddToCartInput();
    }, [props, setAddToCartInput]);

    return (
        <>
            {renderModalPopup}
            <button
                className={classnames(buttonClassName, props.className)}
                aria-label={props.addToCartArialLabel ?? label}
                title={props.addToCartArialLabel ?? label}
                {...attributes}
                onClick={onClickHandler}
                disabled={props.disabled || disabled || isIntermediateState(props) || shouldShowOutOfStock(props, false)}
            >
                {label}
            </button>
            <ItemsAddedToCartDialogComponent
                className='msc-lines-added-to-cart-dialog'
                id={props.id}
                typeName={props.typeName}
                context={props.context}
                dialogStrings={itemsAddedResource}
                data={{
                    products: addToCartInput
                }}
                isModalOpen={isItemsAddedToCartDialogOpen}
                onClose={closeItemsAddedToCartDialog}
            />
            <FileUploadDialog isOpen={isOpen} onConfirm={onUploadFileClick} onCancel={onCancelClick} />
        </>
    );
};

// Set default props
AddToCartFunctionalComponent.defaultProps = {
    quantity: defaultQuantity
};

/**
 * Resolves whether product is in stock.
 * @param props - The add to cart component props.
 * @returns Gets the link text.
 */
const getLinkText = (props: IAddToCartComponentProps): string => {
    return shouldShowOutOfStock(props, false) && props.outOfStockText ? props.outOfStockText : props.addToCartText;
};

/**
 * Resolves whether product is in stock.
 * @param props - The add to cart component props.
 * @returns The add to cart failure result.
 */
const addToCartError = (props: IAddToCartComponentProps): IAddToCartFailureResult | undefined => {
    const {
        data,
        productAvailability,
        isCustomPriceSelected,
        customPriceAmount,
        maximumKeyInPrice,
        minimumKeyInPrice,
        defaultMaximumKeyInPrice = 100,
        defaultMinimumKeyInPrice = 10
    } = props;

    if (!data || !data.product.RecordId) {
        // No product exists, won't be able to add to cart
        return { failureReason: 'EMPTYINPUT' };
    }

    if (data.product.Dimensions) {
        const missingDimensions = data.product.Dimensions.filter(
            dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value)
        );

        if (ArrayExtensions.hasElements(missingDimensions)) {
            // At least one dimension with no value exists on the product, won't be able to add to cart
            return { failureReason: 'MISSINGDIMENSION', missingDimensions };
        }
    }

    if (shouldShowOutOfStock(props, true)) {
        const defaultAvailableQuantity = 0; // Showing as out of stock if no available products found.
        const availableQuantity = productAvailability?.AvailableQuantity ?? defaultAvailableQuantity;
        const stockLeft = Math.max(availableQuantity, defaultAvailableQuantity);

        return { failureReason: 'OUTOFSTOCK', stockLeft };
    }

    // When Custom price is selected, if there is no keyed-in price or keyed-in price is out of limit, should return error.
    if (
        isCustomPriceSelected &&
        (!customPriceAmount ||
            customPriceAmount > (maximumKeyInPrice || defaultMaximumKeyInPrice) ||
            customPriceAmount < (minimumKeyInPrice || defaultMinimumKeyInPrice))
    ) {
        return { failureReason: 'INVALIDCUSTOMAMOUNT' };
    }

    // Only allow adding to cart if not showing out of stock
    return undefined;
};

/**
 * Resolves whether product is in stock.
 * @param props - The add to cart component props.
 * @param includeCurrentQuantity - Flag to specify whether current quantity should be included.
 * @returns The dialog element.
 */
const shouldShowOutOfStock = (props: IAddToCartComponentProps, includeCurrentQuantity: boolean): boolean => {
    if (props.context.app.config.enableStockCheck === undefined || props.context.app.config.enableStockCheck === false) {
        return false;
    }

    // When skip site settings do not need show out of stock on adding to cart
    if (props.shouldSkipSiteSettings) {
        return false;
    }

    if (
        props.isLoading ||
        props.isProductQuantityLoading ||
        props.isUpdatingDimension ||
        props.isLoadingDeliveryOptions ||
        props.isUpdatingDeliveryOptions ||
        props.isAddServiceItemToCart
    ) {
        // Out of stock turn off, don't bother showing out of stock
        return false;
    }

    if (!props.data || !props.data.product.RecordId) {
        // No product exists, don't bother showing out of stock
        return false;
    }

    const hasAvailableProducts = props.hasAvailableProducts ?? true;
    if (!hasAvailableProducts) {
        return true;
    }

    if (props.data.product.Dimensions) {
        if (props.data.product.Dimensions.find(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value))) {
            // At least one dimension with no value exists on the product, so also don't show out of stock
            return false;
        }
    }

    const includedQuantityNumber = includeCurrentQuantity && props.quantity ? props.quantity : defaultQuantity;

    return !(
        props.productAvailability &&
        props.productAvailability.AvailableQuantity !== undefined &&
        props.productAvailability.AvailableQuantity >= includedQuantityNumber
    );
};

const isIntermediateState = (props: IAddToCartComponentProps): boolean => {
    if (props.data?.product.Dimensions) {
        if (props.data.product.Dimensions.find(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value))) {
            // At least one dimension with no value exists on the product, so also not in intermediate state
            return false;
        }
    }

    if (!props.isLoading && !props.isUpdatingDimension && !props.isLoadingDeliveryOptions && !props.isUpdatingDeliveryOptions) {
        return false;
    }

    return true;
};

/*export const AddToCartComponent: React.FunctionComponent<IAddToCartComponentProps> = msdyn365Commerce.createComponentOverride<
    // @ts-expect-error
    IAddtoCartComponent
>('AddToCart', { component: AddToCartFunctionalComponent, ...AddToCartComponentActions });*/

export default AddToCartFunctionalComponent;
